import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { TCandidateRecord } from "./modal";
import { Fetch } from "../../utilities/fetch";
import { JVInput } from "../../shared/JVInput";
import { useToast } from "../../context/toastProvider";
import { TDropdown } from "../../shared/modal";
import { JVDropdown } from "../../shared/JVDropdown";
import { JVDropdownInput } from "../../shared/JVDropdownInput";

const schema = yup.object({
    name: yup.string().required("Candidate Name is required*"),
    email: yup.string().email("Invalid email format").required("Email is required*"),
    phoneNo: yup.string().required("Phone Number is required*")
        .matches(/^[0-9-+() ]*$/, "Phone Number can only contain numbers, spaces, and - + ( ) characters")
        .min(7, "Phone Number must be at least 7 digits").max(15, "Phone Number cannot exceed 15 digits"),
    alternatePhoneNo: yup.string()
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .matches(/^[0-9-+() ]*$/, "Alternate Phone Number can only contain numbers, spaces, and - + ( ) characters")
        .min(7, "Alternate Phone Number must be at least 7 digits").max(15, "Alternate Phone Number cannot exceed 15 digits"),
    experienceInMonth: yup.number().min(0, "Experience cannot be negative").required("Experience is required*"),
    countryId: yup.number().min(1, "Country is required*").required("Country is required*"),
    city: yup.object().shape({
        id: yup.number().min(1, "City is required*").required("City is required*"),
        name: yup.string().required()
    }),
    state: yup.object().shape({
        id: yup.number().min(1, "State is required*").required("State is required*"),
        name: yup.string().required()
    }),
    description: yup.string().nullable()
});

interface Iprops {
    data: TCandidateRecord,
    open: boolean,
    hideDialog: () => void,
    reload: () => void,
}

export const Form = (props: Iprops) => {
    const { showToast } = useToast();
    const [countries, setCountries] = useState<Array<TDropdown>>();
    const [states, setStates] = useState<Array<TDropdown>>();
    const [cities, setCities] = useState<Array<TDropdown>>();
    const [loading, setLoading] = useState(false);

    const { control, handleSubmit, formState: { errors }, reset, watch } = useForm<TCandidateRecord>({
        resolver: yupResolver(schema),
        defaultValues: props.data,
        shouldFocusError: false
    });

    useEffect(() => {
        reset(props.data);
    }, [props.data]);

    const loadCountries = async () => {
        const data = await Fetch<TDropdown[]>(`dropdown/countries`);
        if (data) {
            setCountries(data);
        }
    }

    const countryId = watch('countryId');
    const state = watch('state');

    const loadStates = async (filter: string | null) => {
        if (countryId) {
            const data = await Fetch<TDropdown[]>("dropdown/states",
                { query: `countryId=${countryId}&filter=${filter}` });
            if (data) {
                setStates(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the Country First.", life: 3000 });
        }
    }

    const loadCities = async (filter: string | null) => {
        if (state?.id) {
            const data = await Fetch<TDropdown[]>("dropdown/cities", {
                query: `stateId=${state.id}&filter=${filter}`
            });
            if (data) {
                setCities(data);
            }
        } else {
            showToast({ severity: 'error', detail: "Select the State First.", life: 3000 });
        }
    }


    const completeMethodState = (event: { query: string }) => {
        loadStates(event.query);
    }

    const completeMethodCity = (event: { query: string }) => {
        loadCities(event.query);
    }

    useEffect(() => {
        loadCountries();
    }, []);

    const saveToDB = async (body: TCandidateRecord) => {
        if (loading) return;
        setLoading(true);

        const id = props.data.id;
        const method = id ? "PUT" : "POST";
        const message = id ? "Updated Successfully" : "Added Successfully";

        body.cityId = body.city.id;

        const response = await Fetch(id ? `Candidate/${id}` : 'Candidate', {
            method, body,
            onError: (m) => showToast({ severity: 'error', detail: m })
        });

        if (response) {
            props.hideDialog();
            props.reload();
            showToast({ severity: 'success', detail: message });
        }

        setLoading(false);
    }

    const onHide = () => {
        props.hideDialog();
    }

    function onSubmit(data: TCandidateRecord) {
        saveToDB(data);
    }

    return (
        <Dialog visible={props.open} style={{ width: '80vw' }} header="Add/Edit Candidate" modal className="p-fluid" onHide={onHide}>
            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">

                <JVInput required label="Candidate Name" control={control} name="name" error={errors.name?.message} />
                <JVInput required label="Email Address" control={control} name="email" error={errors.email?.message} />
                <JVInput required label="Phone Number" control={control} name="phoneNo" error={errors.phoneNo?.message} />
                <JVInput label="Alternate PhoneNo" control={control} name="alternatePhoneNo" error={errors.alternatePhoneNo?.message} />
                <JVInput required label="Experience (Months)" type="number" control={control} name="experienceInMonth" error={errors.experienceInMonth?.message} />

                <JVDropdown required column={3} label="Country" control={control} name="countryId" optionLabel="name" optionValue="id" options={countries} placeholder="Select Country" error={errors.countryId?.message} />
                <JVDropdownInput required column={3} completeMethod={completeMethodState} label="State" control={control} name="state" error={errors.state?.id?.message} options={states} optionLabel="name" placeholder="Search State" />
                <JVDropdownInput required column={3} completeMethod={completeMethodCity} label="City" control={control} name="city" error={errors.city?.id?.message} options={cities} optionLabel="name" placeholder="Search City" />

                <div className="col-12 md:col-12">
                    <Button className="field col-12 md:col-6" label="SAVE" type="submit" loading={loading}></Button>
                    <Button className="field col-12 md:col-6 p-button-outlined" label="CLOSE" onClick={onHide} type="button"></Button>
                </div>
            </form>
        </Dialog>
    );
}
